import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
interface ButtonProps extends HTMLMotionProps<'button'> {
  variant?: 'primary' | 'secondary' | 'outline' | 'white';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  href?: string;
  fullWidth?: boolean;
}
export function Button({
  variant = 'primary',
  size = 'md',
  children,
  className = '',
  href,
  fullWidth = false,
  ...props
}: ButtonProps) {
  const baseStyles = 'inline-flex items-center justify-center rounded-lg font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none';
  const variants = {
    primary: 'bg-[#FF6B35] text-white hover:bg-[#e55a2b] focus:ring-[#FF6B35]',
    secondary: 'bg-[#2D3748] text-white hover:bg-[#1a202c] focus:ring-[#2D3748]',
    outline: 'border-2 border-[#FF6B35] text-[#FF6B35] hover:bg-[#FF6B35]/10 focus:ring-[#FF6B35]',
    white: 'bg-white text-[#FF6B35] hover:bg-gray-50 focus:ring-white'
  };
  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  };
  const widthClass = fullWidth ? 'w-full' : '';
  const combinedClassName = `${baseStyles} ${variants[variant]} ${sizes[size]} ${widthClass} ${className}`;
  // Animation props
  const animationProps = {
    whileHover: {
      scale: 1.05
    },
    whileTap: {
      scale: 0.95
    },
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 17
    }
  };
  if (href) {
    // Check if it's an internal anchor link
    const isInternalLink = href.startsWith('#');
    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (isInternalLink) {
        e.preventDefault();
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    };
    return <motion.a href={href} className={combinedClassName} onClick={handleClick} {...!isInternalLink && {
      target: '_blank',
      rel: 'noopener noreferrer'
    }} {...animationProps} data-id="element-206">
        {children}
      </motion.a>;
  }
  return <motion.button className={combinedClassName} {...props} {...animationProps} data-id="element-207">
      {children}
    </motion.button>;
}